import { cloneDeep } from 'lodash'

import { KpiOptionObject, UnitPrefix } from 'types/GlobalKpiOption'
import {
  TKPIValue,
  WidgetData,
  isCoreWidgetFullyLoaded,
  TCoreWidgetSettingsLoaded
} from 'types/GlobalWidget'
import { resolveUnit } from 'utils/metric'
import { formatValue, getKpiOptionFormat } from '../utils'

export const parseValue = (value: string | number | null) => {
  return !!value || value === 0 ? value : '-'
}

export const getPercentage = (value: number, comparativeValue: number) => {
  const difference = getDifference(value, comparativeValue)

  return (difference / Math.abs(comparativeValue)) * 100
}

export const getColor = (difference: number, increaseIsPositive: boolean) => {
  if (difference === 0) {
    return 'warning'
  }

  return (increaseIsPositive && difference >= 0) ||
    (!increaseIsPositive && difference <= 0)
    ? 'success'
    : 'error'
}

export const getDifference = (value: number, comparativeValue: number) => {
  return value - comparativeValue
}

export const getStatus = (
  value: number | null,
  upperThresholdValue: number | null = null,
  lowerThresholdValue: number | null,
  increaseIsPositive: boolean
) => {
  if (upperThresholdValue === null || value == null) {
    return null
  }

  const success = increaseIsPositive
    ? value >= upperThresholdValue
    : value <= upperThresholdValue

  if (success) {
    return 'success'
  }

  const warning =
    lowerThresholdValue !== null &&
    (increaseIsPositive
      ? value > lowerThresholdValue
      : value < lowerThresholdValue)

  if (warning) {
    return 'warning'
  }

  return 'error'
}

export function getKpiOptionsSettings(
  widget: TCoreWidgetSettingsLoaded,
  convertToCurrency?: string | null
) {
  if (!isCoreWidgetFullyLoaded(widget)) {
    return []
  }

  const indices = widget.data.values.map((value) => value.index)
  const sortedOptions = cloneDeep(widget.settings.kpi_options)
    .filter((option) => indices.includes(option.index))
    .sort((a, b) => a.index - b.index)

  if (widget.data.values && widget.data.values.length > 0) {
    return sortedOptions.map((option) => {
      const value = (widget.data as WidgetData).values.find(
        (item) => item.kpi_option_id === option.id
      )

      return parseKpiOption(option, value as TKPIValue, convertToCurrency)
    })
  }

  return []
}

function parseKpiOption(
  option: KpiOptionObject,
  values: TKPIValue,
  convertToCurrency?: string | null
): TParsedKpiValues {
  const increaseIsPositive = option.increase_is_positive
  const {
    equivalentFactor,
    numberOfDecimals,
    significantFigures,
    unitLabel,
    unitPrefix: unitPrefixSelected
  } = getKpiOptionFormat(option, [values.value])
  const value = values.value || 0
  const comparativeValue = values.comparative_value || 0

  const difference = getDifference(value, comparativeValue)
  const percentage = getPercentage(value, comparativeValue)

  const formattedValue = formatValue(
    values.value,
    unitPrefixSelected,
    numberOfDecimals,
    false,
    equivalentFactor,
    significantFigures
  ) as number | null

  return {
    id: option.id,
    comparativeValue,
    increaseIsPositive,
    numberOfDecimals,
    significantFigures,
    unitPrefixSelected,
    equivalentFactor,
    difference,
    percentage,
    value: formatValue(
      values.value,
      unitPrefixSelected,
      numberOfDecimals,
      true,
      equivalentFactor,
      significantFigures
    ),
    status: getStatus(
      formattedValue,
      option.upper_bound_threshold ?? null,
      option.lower_bound_threshold ?? null,
      increaseIsPositive
    ),
    unit: resolveUnit(unitLabel, convertToCurrency) ?? '',
    title: option.title,
    canShowDetails: option.can_show_details
  }
}

export type TParsedKpiValues = {
  id: string
  comparativeValue: number
  increaseIsPositive: boolean
  numberOfDecimals: number
  significantFigures?: number
  unitPrefixSelected: UnitPrefix | null
  equivalentFactor: number | null
  difference: number
  percentage: number
  value: string | number | null
  status: string | null
  unit: string
  title: string
  canShowDetails: boolean
}
